/**
 * Size Up offer block.
 *
 * On the gloves and 250ml cream pages this offers the 250ml Lymphatics Kit and,
 * when taken, collapses the cart to a single 500ml Kit line — the product being
 * viewed is absorbed into the kit rather than sitting beside it, so nobody pays
 * for the gloves twice.
 *
 * On the kit's own page the block is informational. The variant swap there is
 * done at add-to-cart by the theme's existing Promo Upgrades engine.
 */

const route = (name, fallback) => {
  const base = (window.Theme && window.Theme.routes && window.Theme.routes[name]) || fallback;
  return base.endsWith('.js') ? base : `${base}.js`;
};

const getCart = () => fetch(route('cart_url', '/cart')).then((r) => r.json());

class SizeUpOffer extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('[data-add]');
    this.errorEl = this.querySelector('[data-error]');
    this.liveEl = this.querySelector('[data-live]');
    this.confirmEl = this.querySelector('[data-confirm]');
    this.options = [...this.querySelectorAll('[data-choice]')];
    this.totalEl = this.querySelector('[data-total]');
    this.symbol = this.dataset.currencySymbol || '$';

    if (this.button) {
      this.onAdd = this.onAdd.bind(this);
      this.button.addEventListener('click', this.onAdd);
    }

    if (this.options.length) {
      this.onChoose = this.onChoose.bind(this);
      for (const opt of this.options) opt.addEventListener('click', this.onChoose);

      // Falls back to the last option — the offer — if the chosen default is
      // not rendered, e.g. the middle option was left blank.
      const preferred = this.dataset.defaultSelection || 'b';
      const initial =
        this.options.find((o) => o.dataset.choice === preferred) || this.options[this.options.length - 1];
      this.select(initial);

      // The theme re-renders the buy buttons on variant change, which drops the
      // total we appended. Put it back whenever that happens.
      // The sticky add-to-cart bar renders outside the product section and has
      // its own form, so watch the whole document rather than just this section.
      this.observer = new MutationObserver(() => this.paintAtcTotal());
      this.observer.observe(document.body, { childList: true, subtree: true });

      // Last line of defence. Whatever re-rendered, whichever button was
      // pressed, the chosen variant is written into the form that is actually
      // being submitted, in the capture phase — before the theme reads it.
      this.onFormSubmit = this.onFormSubmit.bind(this);
      this.onAtcClick = this.onAtcClick.bind(this);
      document.addEventListener('submit', this.onFormSubmit, true);
      document.addEventListener('click', this.onAtcClick, true);

      this.watchImpression();
    }
  }

  disconnectedCallback() {
    if (this.button && this.onAdd) this.button.removeEventListener('click', this.onAdd);
    if (this.onChoose) for (const opt of this.options) opt.removeEventListener('click', this.onChoose);
    if (this.observer) this.observer.disconnect();
    if (this.impressionObserver) this.impressionObserver.disconnect();
    if (this.onFormSubmit) document.removeEventListener('submit', this.onFormSubmit, true);
    if (this.onAtcClick) document.removeEventListener('click', this.onAtcClick, true);
  }

  /**
   * The handle of the page we are actually on.
   *
   * Liquid cannot supply this from inside the block: a block that declares
   * product settings gets those back from `closest.product`, and the template's
   * own `product` is not in scope either — so data-page-handle arrives as the
   * offered product, not the page. The URL is the one source that cannot be
   * shadowed.
   */
  pageHandle() {
    const match = window.location.pathname.match(/\/products\/([^/?#]+)/);
    return (match && match[1]) || this.dataset.pageHandle || '';
  }

  money(cents) {
    return `${this.symbol}${(Number(cents || 0) / 100).toFixed(2)}`;
  }

  /**
   * Point the product form at whichever option is chosen, so the theme's own
   * Add to cart adds it. One button on the page, no competing actions.
   */
  select(option) {
    if (!option) return;
    this.chosen = option;

    for (const opt of this.options) {
      opt.setAttribute('aria-checked', String(opt === option));
    }

    if (option.dataset.variant) {
      for (const input of this.variantInputs()) input.value = option.dataset.variant;
    }

    if (this.totalEl) this.totalEl.textContent = this.money(option.dataset.price);
    this.paintAtcTotal();
    this.stampOrderProperties(option);
  }

  onChoose(event) {
    const option = event.currentTarget;
    this.select(option);
    // Only a deliberate tap counts as a selection; the default on load does not.
    this.track('select', { choice: option.dataset.choice, items: this.itemFor(option) });
  }

  /** Force the selection onto whichever form is about to be submitted. */
  applyTo(form) {
    if (!form || !this.chosen || !this.chosen.dataset.variant) return;
    const input = form.querySelector('input[name="id"]');
    if (input) input.value = this.chosen.dataset.variant;
    this.stampOrderProperties(this.chosen, form);
  }

  onFormSubmit(event) {
    const form = event.target;
    if (form instanceof HTMLFormElement && form.closest('product-form-component')) this.applyTo(form);
  }

  onAtcClick(event) {
    const button = event.target instanceof Element ? event.target.closest('.add-to-cart-button') : null;
    if (!button) return;
    const form = button.closest('form');
    if (form) this.applyTo(form);
  }

  /**
   * Every product form on the page, not just the first.
   *
   * The PDP renders two: the main buy box and the sticky add-to-cart bar. They
   * are separate forms with their own variant inputs, and on mobile the sticky
   * bar is the one most people actually tap. Writing the selection into only
   * one of them means the other silently adds the page product instead.
   *
   * Scoped to the document rather than the section for the same reason — the
   * sticky bar is rendered outside the product section.
   */
  productForms() {
    return [...document.querySelectorAll('product-form-component form')];
  }

  variantInputs() {
    return [...document.querySelectorAll('product-form-component input[name="id"]')];
  }

  variantInput() {
    return this.variantInputs()[0] || null;
  }

  /**
   * Attribution.
   *
   * Hidden line item properties ride along with the add-to-cart and stay on the
   * order forever — the same mechanism upsell apps use. Underscore-prefixed
   * properties are stored but never shown to the customer, on the order status
   * page or on packing slips.
   *
   * `_su_shipping_tag` records the selected product title so Shopify Flow can
   * copy it to the order's tags for warehouse and Starshipit box filtering.
   *
   * Both options are stamped, not just the upgrade: without the gloves-only
   * rows there is no denominator, and attach rate is unmeasurable.
   */
  stampOrderProperties(option, only) {
    if (this.dataset.track !== 'true') return;
    const forms = only ? [only] : this.productForms();
    if (!forms.length) return;

    const props = {
      _su_offer: this.dataset.offerId || 'size-up',
      _su_page: this.pageHandle(),
      _su_choice: option.dataset.handle || option.dataset.choice || '',
      _su_shipping_tag: option.dataset.title || this.dataset.offerTitle || '',
      _su_upgraded: option.dataset.choice === 'b' ? 'yes' : 'no',
    };

    for (const form of forms) {
      for (const [key, value] of Object.entries(props)) {
        const name = `properties[${key}]`;
        let input = form.querySelector(`input[name="${CSS.escape(name)}"]`);
        if (!input) {
          input = document.createElement('input');
          input.type = 'hidden';
          input.name = name;
          form.appendChild(input);
        }
        input.value = value;
      }
    }
  }

  /**
   * Front-end events, for the funnel the order data cannot show — impressions
   * and option switching. Published to Shopify's pixel bus so a custom web
   * pixel can forward them on, and to dataLayer if GTM is ever added. Both are
   * optional; nothing breaks when neither is present.
   */
  track(event, payload) {
    if (this.dataset.track !== 'true') return;
    const offerId = this.dataset.offerId || 'size-up';
    const detail = { offer_id: offerId, page: this.pageHandle(), ...payload };

    try {
      if (Array.isArray(window.dataLayer)) window.dataLayer.push({ event: `size_up_${event}`, ...detail });
    } catch (e) {
      /* analytics must never break the page */
    }
    try {
      window.Shopify?.analytics?.publish?.(`size_up_${event}`, detail);
    } catch (e) {
      /* ditto */
    }

    // GA4 promotion events. Standard names so this lands in the built-in
    // Promotions report rather than needing a custom exploration.
    const GA4 = { impression: 'view_promotion', select: 'select_promotion' };
    if (GA4[event] && typeof window.gtag === 'function') {
      try {
        window.gtag('event', GA4[event], {
          promotion_id: offerId,
          promotion_name: this.dataset.offerName || offerId,
          creative_slot: this.pageHandle(),
          items: payload.items || [],
        });
      } catch (e) {
        /* ditto */
      }
    }

    document.dispatchEvent(new CustomEvent(`size-up:${event}`, { bubbles: true, detail }));
  }

  /** GA4 item payload for an option. */
  itemFor(option) {
    const label = option.querySelector('.size-up__opt-label');
    return [
      {
        item_id: option.dataset.handle || option.dataset.choice,
        item_name: label ? label.textContent.trim().replace(/\s+/g, ' ') : option.dataset.choice,
        price: Number(option.dataset.price || 0) / 100,
        quantity: 1,
      },
    ];
  }

  /** Fire an impression once the block has actually been scrolled into view. */
  watchImpression() {
    if (this.dataset.track !== 'true' || !('IntersectionObserver' in window)) return;

    this.impressionObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting || this.seen) continue;
          this.seen = true;
          this.track('impression', {
            options: this.options.length,
            items: this.options.flatMap((o) => this.itemFor(o)),
          });
          this.impressionObserver.disconnect();
        }
      },
      { threshold: 0.5 }
    );
    this.impressionObserver.observe(this);
  }

  /** Append the running total to the theme's Add to cart label. */
  paintAtcTotal() {
    if (this.dataset.atcTotal !== 'true' || !this.chosen) return;

    const next = ` · ${this.money(this.chosen.dataset.price)}`;
    for (const label of document.querySelectorAll('.add-to-cart-button .add-to-cart-text')) {
      let el = label.querySelector('.size-up__atc-total');
      if (!el) {
        el = document.createElement('span');
        el.className = 'size-up__atc-total';
        label.appendChild(el);
      }
      if (el.textContent !== next) el.textContent = next;
    }

    // Re-assert the variant on every form — the theme's variant picker and the
    // sticky bar both rewrite these inputs on re-render.
    const want = this.chosen.dataset.variant;
    if (want) {
      for (const input of this.variantInputs()) {
        if (input.value !== want) input.value = want;
      }
      this.stampOrderProperties(this.chosen);
    }
  }

  /** Swap which panel is visible and hide the button once the offer is taken. */
  show(state, confirmText) {
    if (this.dataset.state === state) return;
    this.dataset.state = state;

    for (const name of ['offer', 'applied']) {
      const panel = this.querySelector(`[data-for="${name}"]`);
      if (panel) panel.hidden = name !== state;
    }
    if (this.button) this.button.hidden = state === 'applied';
    if (confirmText && this.confirmEl) this.confirmEl.textContent = confirmText;
  }

  setError(message) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message || '';
    this.errorEl.hidden = !message;
  }

  /**
   * Take the offer.
   *
   * The kit contains the product on this page — the gloves, or the cream — so
   * that line comes out before the kit goes in. Quantity carries across, so
   * two pairs of gloves become two kits rather than one.
   */
  async onAdd() {
    const resolveId = this.dataset.resolveVariantId;
    const companionId = this.dataset.companionVariantId;
    const targetId = resolveId || companionId;
    if (!targetId || !this.button) return;

    this.setError('');
    this.button.setAttribute('aria-busy', 'true');

    try {
      const cart = await getCart();
      const current = this.currentVariantId();

      let quantity = 1;

      if (resolveId) {
        const absorbed = [String(current), String(companionId)].filter((id) => id && id !== 'null');
        const stale = cart.items.filter((item) => absorbed.includes(String(item.variant_id)));

        const currentLines = stale.filter((line) => String(line.variant_id) === String(current));
        if (currentLines.length) {
          quantity = Math.max(...currentLines.map((line) => line.quantity));
        }

        for (const line of stale) {
          await fetch(route('cart_change_url', '/cart/change'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: line.key, quantity: 0 }),
          });
        }
      }

      const properties = {
        _su_offer: this.dataset.offerId || 'size-up',
        _su_page: this.pageHandle(),
        _su_choice: this.dataset.offerTitle || '',
        _su_shipping_tag: this.dataset.offerTitle || '',
        _su_upgraded: 'yes',
      };
      const added = await this.post([{ id: Number(targetId), quantity, properties }]);
      if (!added) return;

      const updated = await getCart();
      const confirmText = this.dataset.addedText || 'Added to your cart.';

      if (this.liveEl) this.liveEl.textContent = confirmText;
      this.show('applied', confirmText);

      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: { resource: updated, sourceId: 'size-up-offer', data: { source: 'size-up-offer' } },
        })
      );
    } catch (e) {
      this.setError('Could not add that just now.');
    } finally {
      this.button.removeAttribute('aria-busy');
    }
  }

  /** POST to cart/add and surface any Shopify error on the block. */
  async post(items) {
    const response = await fetch(route('cart_add_url', '/cart/add'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items }),
    }).then((r) => r.json());

    if (response.status) {
      this.setError(response.description || response.message || 'Could not add that just now.');
      return null;
    }
    return response;
  }

  /** Read the live selection from the product form so variant pickers are honoured. */
  currentVariantId() {
    const scope = this.closest('.shopify-section') || document;
    const input = scope.querySelector('product-form-component input[name="id"]');
    return (input && input.value) || this.dataset.variantId || null;
  }
}

if (!customElements.get('size-up-offer')) {
  customElements.define('size-up-offer', SizeUpOffer);
}
